/**
 * 시간 관련 유틸리티 함수
 */

/**
 * 시간을 "N분 전", "N시간 전" 형식으로 변환
 *
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns "N분 전" 형식의 문자열
 *
 * @example
 * formatTimeAgo("2025-11-02T10:30:00Z") // "3분 전"
 * formatTimeAgo("2025-11-02T08:00:00Z") // "2시간 전"
 * formatTimeAgo("2025-11-01T10:00:00Z") // "1일 전"
 */
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // 밀리초를 각 단위로 변환
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  // 시간에 따라 적절한 형식 반환
  if (seconds < 60) {
    return '방금 전';
  } else if (minutes < 60) {
    return `${minutes}분 전`;
  } else if (hours < 24) {
    return `${hours}시간 전`;
  } else if (days < 7) {
    return `${days}일 전`;
  } else {
    // 7일 이상: 날짜 형식으로 표시
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}월 ${day}일`;
  }
};

/**
 * ISO 8601 날짜 문자열을 "YYYY-MM-DD HH:mm" 형식으로 변환
 *
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns "YYYY-MM-DD HH:mm" 형식의 문자열
 *
 * @example
 * formatDateTime("2025-11-02T10:30:00Z") // "2025-11-02 10:30"
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * ISO 8601 날짜 문자열을 "YY년 MM월 DD일 HH시 MM분 SS초" 형식으로 변환
 *
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns "YY년 MM월 DD일 HH시 MM분 SS초" 형식의 문자열
 *
 * @example
 * formatKoreanDateTime("2025-10-29T14:30:25Z") // "25년 10월 29일 14시 30분 25초"
 */
export const formatKoreanDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const year = String(date.getFullYear()).slice(-2); // 뒤 2자리만 (2025 → 25)
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return `${year}년 ${month}월 ${day}일 ${hours}시 ${minutes}분 ${seconds}초`;
};
